import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import Select from 'react-select'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'

interface NearbyLocation {
    image: File | null
    imagePreview: string
    imageUrl: string
    name: string
    km: string
}

interface FloorPlan {
    image: File | null
    imagePreview: string
    imageUrl: string
    name: string
    size: string
    description: string
}

interface Amenity {
    _id: string;
    name: string;
    icon: string;
}

interface PropertyForm {
    title: string; price: string; area: string; description: string; shortOverview: string; address: string; city: string; state: string; zip: string;
    contactName: string; contactEmail: string; contactPhone: string;
    status: string; type: string; bedrooms: string; bathrooms: string; garage: string; rooms: string; buildingAge: string;
    videoUrl: string;
    subCategory: string; furnishing: string; totalFloors: string; propertyFloor: string; propertySize: string; propertySizeUnit: string;
    facing: string; sellType: string; constructionStatus: string; reraRegistered: boolean; reraNumber: string; balcony: string;
    liftAvailable: boolean; powerBackup: boolean; societyName: string; gatedCommunity: boolean; cornerProperty: boolean; parkFacing: boolean;
    roadWidth: string; nearbyMetro: boolean; parkingType: string; totalParking: string; postedByRole: string;
}

export default function AdminEditProperty() {
    const { token } = useAuth()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [features, setFeatures] = useState<string[]>([])
    const [images, setImages] = useState<File[]>([])
    const [existingImages, setExistingImages] = useState<string[]>([])

    // Nearby locations state
    const [nearbyLocations, setNearbyLocations] = useState<NearbyLocation[]>([])

    // Floor plans state
    const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([])

    // Global amenities management
    const [globalAmenities, setGlobalAmenities] = useState<Amenity[]>([])
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([])

    // Document upload state
    const [brochureUrl, setBrochureUrl] = useState('')
    const [brochureName, setBrochureName] = useState('')
    const [brochureUploading, setBrochureUploading] = useState(false)

    const [legalDocUrl, setLegalDocUrl] = useState('')
    const [legalDocName, setLegalDocName] = useState('')
    const [legalUploading, setLegalUploading] = useState(false)

    const [form, setForm] = useState<PropertyForm>({
        title: '', price: '', area: '', description: '', shortOverview: '', address: '', city: '', state: '', zip: '',
        contactName: '', contactEmail: '', contactPhone: '',
        status: 'For Sale', type: 'Houses', bedrooms: '1', bathrooms: '1', garage: '0', rooms: '0', buildingAge: '',
        videoUrl: '',
        subCategory: '', furnishing: '', totalFloors: '0', propertyFloor: '0', propertySize: '', propertySizeUnit: 'sq.ft',
        facing: '', sellType: '', constructionStatus: '', reraRegistered: false, reraNumber: '', balcony: '0',
        liftAvailable: false, powerBackup: false, societyName: '', gatedCommunity: false, cornerProperty: false, parkFacing: false,
        roadWidth: '', nearbyMetro: false, parkingType: '', totalParking: '0', postedByRole: 'admin'
    })

    const statusOptions = [
        { value: 'For Rent', label: 'For Rent' },
        { value: 'For Sale', label: 'For Sale' },
    ]
    const typeOptions = [
        { value: 'Residential', label: 'Residential' },
        { value: 'Commercial', label: 'Commercial' },
        { value: 'PG/Co-living', label: 'PG/Co-living' },
        { value: 'Plot', label: 'Plot' },
        { value: 'Land', label: 'Land' },
        { value: 'Bank Auction Property', label: 'Bank Auction Property' },
    ]
    const subCategoryMapping: Record<string, string[]> = {
        'Residential': ['Flat', 'Villa', 'Independent House', 'Penthouse', 'Duplex', 'Studio', 'Row House'],
        'Commercial': ['Office Space', 'Shop', 'Showroom', 'Warehouse', 'Co-working Space', 'Industrial Shed'],
        'PG/Co-living': ['PG for Boys', 'PG for Girls', 'Co-living Space', 'Hostel'],
        'Plot': ['Residential Plot', 'Commercial Plot', 'Industrial Plot'],
        'Land': ['Agricultural Land', 'NA Land', 'Industrial Land'],
        'Bank Auction Property': ['Residential', 'Commercial', 'Industrial', 'Land'],
    }
    const numOptions = ['1', '2', '3', '4', '5', '6'].map(v => ({ value: v, label: v }))
    const [ageOptions, setAgeOptions] = useState<{ value: string, label: string }[]>([])
    const allFeatures = ['Air Condition', 'Bedding', 'Heating', 'Internet', 'Microwave', 'Smoking Allow', 'Terrace', 'Balcony', 'Wi-Fi', 'Beach', 'Parking']

    useEffect(() => {
        setAgeOptions(['New', '1-5 Yrs', '10+ Yrs'].map(v => ({ value: v, label: v })))

        // Fetch global amenities
        fetch(`${API_BASE}/amenities`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setGlobalAmenities(data) })
            .catch(console.error)

        // Fetch property data
        if (id) {
            fetch(`${API_BASE}/properties/${id}`)
                .then(res => res.json())
                .then(data => {
                    if (data._id) {
                        setForm({
                            title: data.title || '',
                            price: String(data.price || ''),
                            area: data.area || '',
                            description: data.description || '',
                            shortOverview: data.shortOverview || '',
                            address: data.address || '',
                            city: data.city || '',
                            state: data.state || '',
                            zip: data.zip || '',
                            contactName: data.contactName || '',
                            contactEmail: data.contactEmail || '',
                            contactPhone: data.contactPhone || '',
                            status: data.status || 'For Sale',
                            type: data.type || 'Residential',
                            bedrooms: String(data.bedrooms || '1'),
                            bathrooms: String(data.bathrooms || '1'),
                            garage: String(data.garage || '0'),
                            rooms: String(data.rooms || '0'),
                            buildingAge: data.buildingAge || '',
                            videoUrl: data.videoUrl || '',
                            subCategory: data.subCategory || '',
                            furnishing: data.furnishing || '',
                            totalFloors: String(data.totalFloors || '0'),
                            propertyFloor: String(data.propertyFloor || '0'),
                            propertySize: String(data.propertySize || ''),
                            propertySizeUnit: data.propertySizeUnit || 'sq.ft',
                            facing: data.facing || '',
                            sellType: data.sellType || '',
                            constructionStatus: data.constructionStatus || '',
                            reraRegistered: !!data.reraRegistered,
                            reraNumber: data.reraNumber || '',
                            balcony: String(data.balcony || '0'),
                            liftAvailable: !!data.liftAvailable,
                            powerBackup: !!data.powerBackup,
                            societyName: data.societyName || '',
                            gatedCommunity: !!data.gatedCommunity,
                            cornerProperty: !!data.cornerProperty,
                            parkFacing: !!data.parkFacing,
                            roadWidth: data.roadWidth || '',
                            nearbyMetro: !!data.nearbyMetro,
                            parkingType: data.parkingType || '',
                            totalParking: String(data.totalParking || '0'),
                            postedByRole: data.postedByRole || 'admin'
                        })
                        setFeatures(data.features || [])
                        setExistingImages(data.images || [])
                        setNearbyLocations((data.nearbyLocations || []).map((loc: any) => ({
                            image: null, imagePreview: loc.image, imageUrl: loc.image, name: loc.name, km: loc.km
                        })))
                        setFloorPlans((data.floorPlans || []).map((fp: any) => ({
                            image: null, imagePreview: fp.image, imageUrl: fp.image, name: fp.name, size: fp.size, description: fp.description
                        })))
                        setSelectedAmenities((data.dynamicAmenities || []).map((da: any) => {
                            // Find amenity id by name (assuming name is unique enough for this UI selection)
                            return ''; // This is tricky as dynamicAmenities store name/icon not ID. 
                            // We will handle this by matching names later in submission if needed, 
                            // but for now let's try to match by name in globalAmenities.
                        }))
                    }
                })
                .catch(console.error)
                .finally(() => setLoading(false))
        }
    }, [id])

    // Effect to sync selectedAmenities once globalAmenities and property data are loaded
    useEffect(() => {
        if (id && globalAmenities.length > 0) {
            fetch(`${API_BASE}/properties/${id}`)
                .then(res => res.json())
                .then(data => {
                    const names = (data.dynamicAmenities || []).map((da: any) => da.name)
                    const ids = globalAmenities.filter(ga => names.includes(ga.name)).map(ga => ga._id)
                    setSelectedAmenities(ids)
                })
        }
    }, [id, globalAmenities.length])

    const isResidential = ['Residential', 'PG/Co-living'].includes(form.type)
    const isLand = ['Plot', 'Land'].includes(form.type)
    const isCommercial = ['Commercial', 'Bank Auction Property'].includes(form.type)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }))
    }
    const handleFeature = (f: string) => {
        setFeatures((p: string[]) => p.includes(f) ? p.filter((x: string) => x !== f) : [...p, f])
    }
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setImages(Array.from(e.target.files))
    }

    const removeExistingImage = (url: string) => {
        setExistingImages(p => p.filter(u => u !== url))
    }

    const addNearbyLocation = () => {
        setNearbyLocations(p => [...p, { image: null, imagePreview: '', imageUrl: '', name: '', km: '' }])
    }
    const removeNearbyLocation = (idx: number) => {
        setNearbyLocations((p: NearbyLocation[]) => p.filter((_, i) => i !== idx))
    }
    const updateNearbyField = (idx: number, field: keyof NearbyLocation, value: string) => {
        setNearbyLocations(p => p.map((loc, i) => i === idx ? { ...loc, [field]: value } : loc))
    }
    const handleNearbyImageChange = async (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return
        const file = e.target.files[0]
        const preview = URL.createObjectURL(file)
        setNearbyLocations(p => p.map((loc, i) => i === idx ? { ...loc, image: file, imagePreview: preview } : loc))
        if (!token) return
        const fd = new FormData()
        fd.append('image', file)
        try {
            const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
            })
            const data = await res.json()
            if (data.url) {
                setNearbyLocations(p => p.map((loc, i) => i === idx ? { ...loc, imageUrl: data.url } : loc))
            }
        } catch (err) { console.error('Nearby image upload failed', err) }
    }

    // --- Floor Plans ---
    const addFloorPlan = () => {
        setFloorPlans(p => [...p, { image: null, imagePreview: '', imageUrl: '', name: '', size: '', description: '' }])
    }
    const removeFloorPlan = (idx: number) => {
        setFloorPlans(p => p.filter((_, i) => i !== idx))
    }
    const updateFloorPlanField = (idx: number, field: keyof FloorPlan, value: string) => {
        setFloorPlans(p => p.map((fp, i) => i === idx ? { ...fp, [field]: value } : fp))
    }
    const handleFloorPlanImageChange = async (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return
        const file = e.target.files[0]
        const preview = URL.createObjectURL(file)
        setFloorPlans(p => p.map((fp, i) => i === idx ? { ...fp, image: file, imagePreview: preview } : fp))
        if (!token) return
        const fd = new FormData()
        fd.append('image', file)
        try {
            const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
            })
            const data = await res.json()
            if (data.url) {
                setFloorPlans(p => p.map((fp, i) => i === idx ? { ...fp, imageUrl: data.url } : fp))
            }
        } catch (err) { console.error('Floor plan image upload failed', err) }
    }

    // --- Dynamic Amenities ---
    const toggleAmenity = (id: string) => {
        setSelectedAmenities(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    }

    const handleBrochureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !token) return
        const file = e.target.files[0]
        setBrochureUploading(true)
        const fd = new FormData()
        fd.append('document', file)
        try {
            const res = await fetch(`${API_BASE}/upload/document`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
            })
            const data = await res.json()
            if (res.ok && data.url) {
                setBrochureUrl(data.url)
                setBrochureName(data.filename || file.name)
            }
        } catch (err) { console.error('Brochure upload failed', err) }
        setBrochureUploading(false)
    }

    const handleLegalDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !token) return
        const file = e.target.files[0]
        setLegalUploading(true)
        const fd = new FormData()
        fd.append('document', file)
        try {
            const res = await fetch(`${API_BASE}/upload/document`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
            })
            const data = await res.json()
            if (res.ok && data.url) {
                setLegalDocUrl(data.url)
                setLegalDocName(data.filename || file.name)
            }
        } catch (err) { console.error('Legal doc upload failed', err) }
        setLegalUploading(false)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token || !id) { setMsg({ type: 'error', text: 'Operation failed.' }); return }
        if (!form.title || !form.price) { setMsg({ type: 'error', text: 'Title and Price are required.' }); return }
        setSubmitting(true); setMsg(null)

        let newImageUrls: string[] = []
        if (images.length > 0) {
            try {
                const uploadRes = await api.uploadImages(token, images)
                if (uploadRes.urls) newImageUrls = uploadRes.urls
            } catch (err) {
                setMsg({ type: 'error', text: 'Image upload failed.' })
                setSubmitting(false); return
            }
        }

        const imageUrls = [...existingImages, ...newImageUrls]

        const nearbyData = nearbyLocations.map(loc => ({
            name: loc.name, km: loc.km, image: loc.imageUrl
        }))

        const floorPlanData = floorPlans.map(fp => ({
            name: fp.name, size: fp.size, description: fp.description, image: fp.imageUrl
        }))

        const dynamicAmenitiesData = globalAmenities
            .filter(a => selectedAmenities.includes(a._id))
            .map(a => ({ name: a.name, icon: a.icon }))

        try {
            const res = await api.adminUpdateProperty(token, id, {
                ...form,
                price: Number(form.price),
                bedrooms: Number(form.bedrooms),
                bathrooms: Number(form.bathrooms),
                garage: Number(form.garage),
                rooms: Number(form.rooms),
                propertySize: Number(form.propertySize || 0),
                totalFloors: Number(form.totalFloors),
                propertyFloor: Number(form.propertyFloor),
                balcony: Number(form.balcony),
                totalParking: Number(form.totalParking),
                features,
                dynamicAmenities: dynamicAmenitiesData,
                images: imageUrls,
                nearbyLocations: nearbyData,
                floorPlans: floorPlanData,
                shortOverview: form.shortOverview,
                brochureUrl,
                brochureName,
                legalDocUrl,
                legalDocName
            })
            if (res._id) {
                setMsg({ type: 'success', text: 'Property updated successfully!' })
                setTimeout(() => navigate('/admin-properties'), 2000)
            } else {
                setMsg({ type: 'error', text: res.message || 'Failed to update property.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        }
        setSubmitting(false)
    }

    if (loading) {
        return (
            <AdminLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Loading property details...</p>
                </div>
            </AdminLayout>
        )
    }

    return (
        <AdminLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-bold mb-1">Edit Property</h3>
                    <p className="text-muted mb-0">Modify the property details below.</p>
                </div>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    {msg.text}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="row g-4">
                    <div className="col-lg-8">
                        {/* Basic Info Card */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Basic Information</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold text-muted small uppercase">Property Title <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="title" value={form.title} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-muted small uppercase">Status</label>
                                        <Select options={statusOptions} value={statusOptions.find(o => o.value === form.status)}
                                            styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                            onChange={opt => setForm(p => ({ ...p, status: opt?.value || 'For Sale' }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-muted small uppercase">Property Type</label>
                                        <Select options={typeOptions} value={typeOptions.find(o => o.value === form.type)}
                                            styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                            onChange={opt => setForm(p => ({ ...p, type: opt?.value || 'Residential', subCategory: '' }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-muted small uppercase">Price (₹) <span className="text-danger">*</span></label>
                                        <input type="number" className="form-control bg-light border-0 py-2" name="price" value={form.price} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-muted small uppercase">Area (sq ft)</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="area" value={form.area} onChange={handleChange} />
                                    </div>
                                    {isResidential && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">Bedrooms</label>
                                            <Select options={numOptions} value={numOptions.find(o => o.value === form.bedrooms)}
                                                styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                                onChange={opt => setForm(p => ({ ...p, bedrooms: opt?.value || '1' }))} />
                                        </div>
                                    )}
                                    {(isResidential || isCommercial) && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">{isCommercial ? 'Washrooms' : 'Bathrooms'}</label>
                                            <Select options={numOptions} value={numOptions.find(o => o.value === form.bathrooms)}
                                                styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                                onChange={opt => setForm(p => ({ ...p, bathrooms: opt?.value || '1' }))} />
                                        </div>
                                    )}
                                    {(isResidential || isCommercial) && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">Total Rooms</label>
                                            <Select options={numOptions} value={numOptions.find(o => o.value === form.rooms)}
                                                styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                                onChange={opt => setForm(p => ({ ...p, rooms: opt?.value || '0' }))} />
                                        </div>
                                    )}
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-muted small uppercase">Sub Category</label>
                                        <Select
                                            key={form.type}
                                            options={(subCategoryMapping[form.type] || []).map(v => ({ value: v, label: v }))}
                                            value={form.subCategory ? { value: form.subCategory, label: form.subCategory } : null}
                                            styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                            onChange={opt => setForm(p => ({ ...p, subCategory: opt?.value || '' }))} />
                                    </div>
                                    {!isLand && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">Furnishing Status</label>
                                            <Select options={['Furnished', 'Semi Furnished', 'Unfurnished'].map(v => ({ value: v, label: v }))}
                                                value={form.furnishing ? { value: form.furnishing, label: form.furnishing } : null}
                                                styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                                onChange={opt => setForm(p => ({ ...p, furnishing: opt?.value || '' }))} />
                                        </div>
                                    )}
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-muted small uppercase">Facing</label>
                                        <Select options={['North', 'South', 'East', 'West', 'N-E', 'N-W', 'S-E', 'S-W'].map(v => ({ value: v, label: v }))}
                                            value={form.facing ? { value: form.facing, label: form.facing } : null}
                                            styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                            onChange={opt => setForm(p => ({ ...p, facing: opt?.value || '' }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-muted small uppercase">Property Size (sq.ft)</label>
                                        <input type="number" className="form-control bg-light border-0 py-2" name="propertySize" value={form.propertySize} onChange={handleChange} placeholder="Numeric value" />
                                    </div>
                                    {!isLand && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">Sell Type</label>
                                            <Select options={['New', 'Resale'].map(v => ({ value: v, label: v }))}
                                                value={form.sellType ? { value: form.sellType, label: form.sellType } : null}
                                                styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                                onChange={opt => setForm(p => ({ ...p, sellType: opt?.value || '' }))} />
                                        </div>
                                    )}
                                    {!isLand && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">Construction Status</label>
                                            <Select options={['Ready to Move', 'Under Construction'].map(v => ({ value: v, label: v }))}
                                                value={form.constructionStatus ? { value: form.constructionStatus, label: form.constructionStatus } : null}
                                                styles={{ control: (base) => ({ ...base, background: '#f8fafc', border: 0, borderRadius: '8px', padding: '2px' }) }}
                                                onChange={opt => setForm(p => ({ ...p, constructionStatus: opt?.value || '' }))} />
                                        </div>
                                    )}
                                    {isResidential && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">Balcony</label>
                                            <input type="number" className="form-control bg-light border-0 py-2" name="balcony" value={form.balcony} onChange={handleChange} />
                                        </div>
                                    )}
                                    <div className="col-md-4">
                                        <div className="form-check mt-4 pt-1">
                                            <input className="form-check-input" type="checkbox" id="reraCheck" checked={form.reraRegistered} onChange={e => setForm(p => ({ ...p, reraRegistered: e.target.checked }))} />
                                            <label className="form-check-label small fw-bold text-muted uppercase" htmlFor="reraCheck">RERA Registered</label>
                                        </div>
                                    </div>
                                    {form.reraRegistered && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold text-muted small uppercase">RERA Number</label>
                                            <input type="text" className="form-control bg-light border-0 py-2" name="reraNumber" value={form.reraNumber} onChange={handleChange} />
                                        </div>
                                    )}
                                    <div className="col-12">
                                        <label className="form-label fw-bold text-muted small uppercase">Short Overview</label>
                                        <textarea className="form-control bg-light border-0 py-2" rows={2} name="shortOverview" value={form.shortOverview} onChange={handleChange} placeholder="Brief highlight of the property..."></textarea>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-bold text-muted small uppercase">Description <span className="text-danger">*</span></label>
                                        <textarea className="form-control bg-light border-0 py-2" rows={4} name="description" value={form.description} onChange={handleChange} placeholder="Describe the property details..." required></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Extra Amenities Toggle Card */}
                        {!isLand && (
                            <div className="card border-0 shadow-sm rounded-4 mb-4">
                                <div className="card-header bg-white py-3 border-bottom">
                                    <h5 className="mb-0 fw-bold">Advanced Search Amenities</h5>
                                </div>
                                <div className="card-body p-4">
                                    <div className="row g-3">
                                        {[
                                            { key: 'liftAvailable', label: 'Lift Available' },
                                            { key: 'powerBackup', label: 'Power Backup' },
                                            { key: 'gatedCommunity', label: 'Gated Community' },
                                            { key: 'cornerProperty', label: 'Corner Property' },
                                            { key: 'parkFacing', label: 'Park Facing' },
                                            { key: 'nearbyMetro', label: 'Nearby Metro' }
                                        ].map((v, i) => (
                                            <div className="col-md-4 col-6" key={i}>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="checkbox" id={`adv-${v.key}`} checked={form[v.key as keyof PropertyForm] as boolean} onChange={e => setForm(p => ({ ...p, [v.key]: e.target.checked }))} />
                                                    <label className="form-check-label small fw-medium ms-1" htmlFor={`adv-${v.key}`}>{v.label}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Location Card */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Location Details</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold text-muted small uppercase">Address</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="address" value={form.address} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-muted small uppercase">City</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="city" value={form.city} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-muted small uppercase">State</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="state" value={form.state} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold text-muted small uppercase">Zip Code</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="zip" value={form.zip} onChange={handleChange} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Features Card */}
                        {!isLand && (
                            <div className="card border-0 shadow-sm rounded-4 mb-4">
                                <div className="card-header bg-white py-3 border-bottom">
                                    <h5 className="mb-0 fw-bold">Property Features</h5>
                                </div>
                                <div className="card-body p-4">
                                    <div className="row g-3">
                                        {allFeatures.map((feat, i) => (
                                            <div className="col-md-3 col-6" key={i}>
                                                <div className="form-check custom-checkbox">
                                                    <input type="checkbox" className="form-check-input" id={`feat-${i}`}
                                                        checked={features.includes(feat)} onChange={() => handleFeature(feat)} />
                                                    <label className="form-check-label small fw-medium ms-1" htmlFor={`feat-${i}`}>{feat}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Global Dynamic Amenities Checkboxes */}
                        {!isLand && (
                            <div className="card border-0 shadow-sm rounded-4 mb-4">
                                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                                    <h5 className="mb-0 fw-bold">Property Amenities (with Icons)</h5>
                                    <Link to="/admin-amenities" className="btn btn-sm btn-outline-primary rounded-pill">
                                        <i className="fa-solid fa-gear me-1"></i> Manage
                                    </Link>
                                </div>
                                <div className="card-body p-4">
                                    {globalAmenities.length === 0 ? (
                                        <div className="text-center py-4 bg-light rounded-3">
                                            <p className="text-muted mb-0">No global amenities found. Please add some first.</p>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            {globalAmenities.map((am) => (
                                                <div className="col-lg-3 col-md-4 col-6" key={am._id}>
                                                    <div
                                                        className={`p-2 rounded-3 border d-flex align-items-center gap-2 cursor-pointer transition-all ${selectedAmenities.includes(am._id) ? 'bg-primary-subtle border-primary' : 'bg-light border-transparent'}`}
                                                        onClick={() => toggleAmenity(am._id)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="form-check m-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                checked={selectedAmenities.includes(am._id)}
                                                                onChange={() => { }}
                                                            />
                                                        </div>
                                                        <img src={getImageUrl(am.icon)} alt={am.name} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                                        <span className="small fw-bold text-dark text-truncate">{am.name}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Floor Plans Card */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                                <h5 className="mb-0 fw-bold">Floor Plans</h5>
                                <button type="button" className="btn btn-sm btn-primary" onClick={addFloorPlan}>
                                    <i className="fa-solid fa-plus me-1"></i> Add +
                                </button>
                            </div>
                            <div className="card-body p-4">
                                {floorPlans.length === 0 ? (
                                    <div className="text-center py-4 bg-light rounded-3 border-dashed" style={{ border: '2px dashed #cbd5e1' }}>
                                        <p className="text-muted mb-0">No floor plans added yet.</p>
                                    </div>
                                ) : (
                                    <div className="row g-4">
                                        {floorPlans.map((fp, idx) => (
                                            <div className="col-12" key={idx}>
                                                <div className="p-3 bg-light rounded-3 position-relative">
                                                    <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2" onClick={() => removeFloorPlan(idx)}>
                                                        <i className="fa-solid fa-xmark"></i>
                                                    </button>
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label fw-bold text-muted small uppercase">Plan Image</label>
                                                            <input type="file" className="form-control form-control-sm border-0" onChange={(e) => handleFloorPlanImageChange(idx, e)} />
                                                            {fp.imagePreview && <img src={getImageUrl(fp.imagePreview)} className="mt-2 rounded-2 shadow-sm" style={{ width: '100%', height: '100px', objectFit: 'cover' }} />}
                                                        </div>
                                                        <div className="col-md-8">
                                                            <div className="row g-3">
                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-bold text-muted small uppercase">Plan Name</label>
                                                                    <input type="text" className="form-control form-control-sm bg-white border-0" value={fp.name} onChange={e => updateFloorPlanField(idx, 'name', e.target.value)} placeholder="e.g. Ground Floor" />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-bold text-muted small uppercase">Plan Size</label>
                                                                    <input type="text" className="form-control form-control-sm bg-white border-0" value={fp.size} onChange={e => updateFloorPlanField(idx, 'size', e.target.value)} placeholder="e.g. 1200 sq ft" />
                                                                </div>
                                                                <div className="col-12">
                                                                    <label className="form-label fw-bold text-muted small uppercase">Description</label>
                                                                    <textarea className="form-control form-control-sm bg-white border-0" rows={2} value={fp.description} onChange={e => updateFloorPlanField(idx, 'description', e.target.value)} placeholder="Brief description of this floor..."></textarea>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Nearby Locations Card */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                                <h5 className="mb-0 fw-bold">Nearby Locations</h5>
                                <button type="button" className="btn btn-sm btn-success" onClick={addNearbyLocation}>
                                    <i className="fa-solid fa-plus me-1"></i> Add +
                                </button>
                            </div>
                            <div className="card-body p-4">
                                {nearbyLocations.length === 0 ? (
                                    <div className="text-center py-4 bg-light rounded-3 border-dashed" style={{ border: '2px dashed #cbd5e1' }}>
                                        <p className="text-muted mb-0">No nearby locations added yet.</p>
                                    </div>
                                ) : (
                                    <div className="row g-3">
                                        {nearbyLocations.map((loc, idx) => (
                                            <div className="col-12" key={idx}>
                                                <div className="p-3 bg-light rounded-3 position-relative">
                                                    <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2" onClick={() => removeNearbyLocation(idx)}>
                                                        <i className="fa-solid fa-xmark"></i>
                                                    </button>
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <input type="file" className="form-control form-control-sm border-0" onChange={(e) => handleNearbyImageChange(idx, e)} />
                                                            {loc.imagePreview && <img src={getImageUrl(loc.imagePreview)} className="mt-2 rounded-2 shadow-sm" style={{ width: '100%', height: '80px', objectFit: 'cover' }} />}
                                                        </div>
                                                        <div className="col-md-5">
                                                            <input type="text" className="form-control form-control-sm bg-white border-0" value={loc.name} onChange={e => updateNearbyField(idx, 'name', e.target.value)} placeholder="Location name" />
                                                        </div>
                                                        <div className="col-md-3">
                                                            <input type="text" className="form-control form-control-sm bg-white border-0" value={loc.km} onChange={e => updateNearbyField(idx, 'km', e.target.value)} placeholder="Distance (KM)" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div> {/* End col-lg-8 */}

                    <div className="col-lg-4">
                        {/* Gallery */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Property Gallery</h5>
                            </div>
                            <div className="card-body p-4 text-center">
                                <div className="mb-4 d-flex flex-wrap gap-2">
                                    {existingImages.map((url, i) => (
                                        <div key={i} className="position-relative" style={{ width: '80px', height: '80px' }}>
                                            <img src={getImageUrl(url)} className="w-100 h-100 rounded object-fit-cover" alt="Property" />
                                            <button type="button" className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 m-1 circle shadow-sm" style={{ width: '20px', height: '20px', fontSize: '10px' }} onClick={() => removeExistingImage(url)}>
                                                <i className="fa-solid fa-xmark"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="upload-area border-dashed p-4 rounded-3 bg-light" style={{ border: '2px dashed #cbd5e1' }}>
                                    <i className="fa-solid fa-cloud-arrow-up fa-3x text-success mb-3"></i>
                                    <h5>Upload New Images</h5>
                                    <p className="text-muted small">Select additional images</p>
                                    <input type="file" className="form-control" multiple onChange={handleImageChange} accept="image/*" />
                                </div>
                                {images.length > 0 && <p className="mt-2 text-success small fw-bold mb-0">{images.length} new images selected</p>}
                            </div>
                        </div>

                        {/* Video */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Property Video</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold small text-muted text-uppercase">YouTube Video URL</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><i className="fa-brands fa-youtube text-danger"></i></span>
                                            <input type="text" className="form-control bg-light border-0 py-2" name="videoUrl" value={form.videoUrl} onChange={handleChange} placeholder="e.g., https://www.youtube.com/watch?v=yba7hPeTSjk" />
                                        </div>
                                        <p className="text-muted small mt-2">Paste the full YouTube URL here (e.g., https://www.youtube.com/watch?v=...) to show a property video.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Documents Card */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Documents</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-muted small uppercase">Brochure (PDF)</label>
                                    <input type="file" className="form-control form-control-sm border-0 bg-light" onChange={handleBrochureUpload} />
                                    {brochureUploading && <small className="text-primary">Uploading...</small>}
                                    {brochureUrl && <small className="text-success d-block text-truncate mt-1"><i className="fa-solid fa-check me-1"></i>{brochureName}</small>}
                                </div>
                                <div>
                                    <label className="form-label fw-bold text-muted small uppercase">Legal Document</label>
                                    <input type="file" className="form-control form-control-sm border-0 bg-light" onChange={handleLegalDocUpload} />
                                    {legalUploading && <small className="text-primary">Uploading...</small>}
                                    {legalDocUrl && <small className="text-success d-block text-truncate mt-1"><i className="fa-solid fa-check me-1"></i>{legalDocName}</small>}
                                </div>
                            </div>
                        </div>

                        {/* Admin Contact Card */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Contact Info</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-muted small uppercase">Contact Name</label>
                                    <input type="text" className="form-control bg-light border-0 py-2 w-100" style={{ borderRadius: '8px' }} name="contactName" value={form.contactName} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-muted small uppercase">Contact Email</label>
                                    <input type="email" className="form-control bg-light border-0 py-2 w-100" style={{ borderRadius: '8px' }} name="contactEmail" value={form.contactEmail} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className="form-label fw-bold text-muted small uppercase">Contact Phone</label>
                                    <input type="text" className="form-control bg-light border-0 py-2 w-100" style={{ borderRadius: '8px' }} name="contactPhone" value={form.contactPhone} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div className="d-grid gap-2">
                            <button className="btn btn-primary py-3 rounded-4 fw-bold shadow-sm" type="submit" disabled={submitting}>
                                {submitting ? <><span className="spinner-border spinner-border-sm me-2"></span>Updating...</> : 'Update Property'}
                            </button>
                            <Link to="/admin-properties" className="btn btn-light py-3 rounded-4 fw-bold">Cancel</Link>
                        </div>
                    </div> {/* End col-lg-4 */}
                </div> {/* End row */}
            </form>
        </AdminLayout >
    )
}


